import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import Datepicker from './Datepicker';
import { getMillisFromDateString } from 'utils/time';
import { getDateFormat } from 'utils/teamHelper';

import _ from 'underscore';

import { EventValidator } from '@shoflo/polymer/validators/EventValidator/EventValidator';
import { LabelList } from '@shoflo/polymer/components/label/LabelList';
import { LabelPicker } from '@shoflo/polymer/components/label/LabelPicker';
import { TimeZonePicker } from '@shoflo/polymer/components/TimeZonePicker/TimeZonePicker';
import { UserPicker } from '@shoflo/polymer/components/UserPicker/UserPicker';
import { useFormState } from 'react-use-form-state';

import {
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';

import { Button } from '@shoflo/polymer/components/Button/Button';
import styled from 'styled-components';
import PhotoInput from 'components/PhotoInput';

import StyledFormLabel from 'components/form/StyledFormLabel';
import Tooltip from 'components/Tooltip';

const StyledLabelGroup = styled(FormGroup)`
  display: flex;
`;

const ImageSizeLabel = styled.label`
  display: block;
  width: 100%;
  color: #767676;
  font-size: 12px;
`;

const OwnerWrapper = styled.div`
  display: flex;
  align-items: center;

  span {
    display: flex;
    align-items: center;

    select {
      margin-left: 10px;
    }
  }
`;

const StyledCancelButton = styled(Button)`
  color: #767676;
`;

export const handleDateChange = (
  dateType,
  setStartDate,
  setEndDate,
  dateFormat,
  day,
) => {
  if (dateType === 'startDate') {
    if (day) {
      setStartDate(moment(day).format(dateFormat));
      setEndDate(moment(day).format(dateFormat));
    } else {
      setStartDate(undefined);
    }
    return 'startDate';
  } else {
    setEndDate(day ? moment(day).format(dateFormat) : day);

    return 'endDate';
  }
};

const EditForm = ({
  team,
  event,
  onSubmit,
  onCancel,
  onLabelCreated,
  labels,
  owners,
  isTeamAdmin,
}) => {
  const [selectedLabelIds, setSelectedLabelIds] = useState(
    event.labels.map((l) => l._id || l),
  );

  const [photoFile, setPhotoFile] = useState();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const dateFormat = getDateFormat(team) || {};

  const [startDate, setStartDate] = useState(
    moment.tz(event.startOn, event.timeZone).format(dateFormat),
  );
  const [endDate, setEndDate] = useState(
    moment.tz(event.endOn, event.timeZone).format(dateFormat),
  );
  const [resetEventChatConfirmation, setResetEventChatConfirmation] =
    useState(false);

  const defaultOwnerId = owners && owners.length > 0 ? owners[0]._id : null;

  const initialFormState = useMemo(() => {
    const baseState = {
      title: event.title,
      venue: event.venue,
      timeZone: event.timeZone,
      twentyFourHourClock: event.twentyFourHourClock
        ? event.twentyFourHourClock.toString()
        : 'false',
      startOn: moment(event.startOn).format(dateFormat),
      endOn: moment(event.endOn).format(dateFormat),
    };

    if (isTeamAdmin) {
      baseState.owner = event.owner ? event.owner._id : defaultOwnerId;
    }

    return baseState;
  }, [event, dateFormat, isTeamAdmin, defaultOwnerId]);

  const [formState, { label, text, select, raw, radio }] = useFormState(initialFormState);

  const { validity, values } = formState;

  return (
    <div>
      <Form
        data-testid="eventSettingsModal"
        onSubmit={(e) => {
          e.preventDefault();
          setIsSubmitting(true);
          const { timeZone } = values;
          onSubmit(
            {
              ...formState.values,
              twentyFourHourClock:
                formState.values.twentyFourHourClock === 'true',
              startOn: getMillisFromDateString(startDate, dateFormat, timeZone),
              endOn: getMillisFromDateString(endDate, dateFormat, timeZone),
            },
            photoFile,
            selectedLabelIds,
          );
        }}
      >
        <FormGroup>
          <StyledFormLabel {...label('title')}>Name</StyledFormLabel>
          <Tooltip
            id="event-edit-form-name"
            title="This text will identify this Event"
          />
          <Input
            data-testid="eventTitle"
            aria-labelledby="eventTitleInput"
            aria-describedby="tip-event-edit-form-name"
            {...text({
              name: 'title',
              validate: EventValidator.title,
              validateOnBlur: false,
              touchOnChange: true,
            })}
            invalid={formState.touched.title && !formState.validity.title}
            required
          />
          {validity.title === false && (
            <FormFeedback>
              Uh oh! Event names must be at least 2 characters.
            </FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <StyledFormLabel {...label('venue')}>Location</StyledFormLabel>
          <Tooltip
            id="event-edit-form-location"
            title="Where is this Event taking place"
          />
          <Input
            data-testid="eventLocation"
            aria-labelledby="eventLocationInput"
            aria-describedby="tip-event-edit-form-location"
            {...text({
              name: 'venue',
              validateOnBlur: false,
              touchOnChange: true,
            })}
          />
        </FormGroup>

        <Row form>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel {...label('startOn')}>
                Start Date
              </StyledFormLabel>
              <Datepicker
                title="startOn"
                format={dateFormat}
                dataTest="startDate"
                value={startDate}
                onChange={handleDateChange.bind(
                  null,
                  'startDate',
                  setStartDate,
                  setEndDate,
                  dateFormat,
                )}
                tooltipProps={{
                  id: 'event-edit-form-start-date',
                  title: 'When does this Event begin?',
                }}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel {...label('endOn')}>End Date</StyledFormLabel>
              <Datepicker
                title="endOn"
                format={dateFormat}
                dataTest="endDate"
                value={endDate}
                onChange={handleDateChange.bind(
                  null,
                  'endDate',
                  setStartDate,
                  setEndDate,
                  dateFormat,
                )}
                tooltipProps={{
                  id: 'event-edit-form-end-date',
                  title:
                    'When does this Event End? Note: that this date will be used to automatically archive the Event',
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row form>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel htmlFor="timeZone">Time Zone</StyledFormLabel>
              <Tooltip
                id="event-edit-form-timezone"
                title="This setting will affect the time of day clock for all files in this Event"
              />
              <FormGroup data-testid="timeZonePicker">
                <TimeZonePicker
                  {...select({
                    name: 'timeZone',
                  })}
                  aria-describedby="tip-event-edit-form-timezone"
                />
              </FormGroup>
            </FormGroup>
          </Col>
          <Col md={6}>
            <Tooltip
              id="event-edit-form-time-format"
              title="This setting will affect the time of day clock for all files in this Event"
            />
            <StyledFormLabel aria-describedby="tip-event-edit-form-time-format">
              Time Format
            </StyledFormLabel>
            <FormGroup check>
              <Label check>
                <Input
                  {...radio({ name: 'twentyFourHourClock', value: false })}
                  data-testid="12HourButton"
                  id="12hour"
                  checked={values.twentyFourHourClock === 'false'}
                />{' '}
                12-hour
              </Label>
              <br />
              <Label check>
                <Input
                  {...radio({ name: 'twentyFourHourClock', value: true })}
                  data-testid="24HourButton"
                  id="24hour"
                  checked={values.twentyFourHourClock === 'true'}
                />{' '}
                24-hour
              </Label>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            {isTeamAdmin && (
              <>
                <Tooltip
                  id="event-edit-form-labels"
                  title="A colorful label used to quickly identify a property of an event"
                />
                <StyledFormLabel aria-describedby="tip-event-edit-form-labels">
                  Label(s)
                </StyledFormLabel>
                <StyledLabelGroup>
                  <LabelList
                    labels={selectedLabelIds.map((id) => {
                      const label = _.find(labels, (label) => label._id === id);
                      return {
                        ...label,
                        onClick: () => {
                          setSelectedLabelIds(
                            selectedLabelIds.filter((id) => label._id !== id),
                          );
                        },
                      };
                    })}
                  />
                  <LabelPicker
                    labels={labels}
                    selectedLabelIds={selectedLabelIds}
                    onCreate={onLabelCreated}
                    onChange={setSelectedLabelIds}
                  />
                </StyledLabelGroup>
              </>
            )}
          </Col>
        </Row>
        <Row form>
          <Col md={6}>
            <PhotoInput
              label="Event branding"
              imageUri={event.photo}
              onChange={setPhotoFile}
              type="event"
              tooltipProps={{
                id: 'event-edit-form-branding',
                placement: 'bottom-start',
                title:
                  'Select the image which will identify this event in Shoflo and on Printouts',
              }}
            />
            <ImageSizeLabel>JPG, GIF, or PNG. Max size of 2mb.</ImageSizeLabel>
          </Col>
          <Col md={6}>
            {isTeamAdmin && (
              <>
                <Tooltip
                  id="event-edit-form-owner"
                  title="Who is the point person for this Event?"
                />
                <StyledFormLabel aria-describedby="tip-event-edit-form-owner">
                  Owner
                </StyledFormLabel>
                <OwnerWrapper>
                  <UserPicker
                    {...raw({
                      name: 'owner',
                    })}
                    id="owner"
                    users={owners}
                  />
                </OwnerWrapper>
              </>
            )}
          </Col>
        </Row>
        <div className="float-end modal-footer">
          <Button
            data-testid="saveButton"
            color="primary"
            disabled={isSubmitting}
          >
            Save
          </Button>{' '}
          <Button
            data-testid="cancelButton"
            color="secondary"
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

EditForm.propTypes = {
  team: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onLabelCreated: PropTypes.func.isRequired,
  labels: PropTypes.array.isRequired,
  owners: PropTypes.array.isRequired,
  isTeamAdmin: PropTypes.bool.isRequired,
};

export default EditForm;
